import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  FileCheck2,
  ListChecks,
  Search,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { getApiErrorMessage, portalApi } from "../api/client";
import type { ApiEnvelope } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { RequirementRow } from "../compliance/RequirementRow";
import { PageError } from "../components/PageError";
import type { PortalDashboard } from "../types/compliance";
import { Button } from "../components/ui";

type PageState =
  | { status: "loading" }
  | { status: "forbidden" }
  | { status: "error"; message: string }
  | { status: "ready"; data: PortalDashboard };

export function DashboardPage() {
  const { me } = useAuth();
  const [state, setState] = React.useState<PageState>({ status: "loading" });

  const load = React.useCallback(async () => {
    setState({ status: "loading" });
    try {
      const response = await portalApi.get<ApiEnvelope<PortalDashboard>>(
        "/portal/dashboard"
      );
      setState({ status: "ready", data: response.data.data });
    } catch (requestError: any) {
      if (requestError.response?.status === 403) {
        setState({ status: "forbidden" });
      } else {
        setState({
          status: "error",
          message: getApiErrorMessage(
            requestError,
            "Não foi possível carregar o dashboard."
          ),
        });
      }
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "loading") return <DashboardSkeleton />;
  if (state.status === "forbidden") {
    return (
      <PageError
        title="Acesso não permitido"
        description="Seu perfil não possui permissão para visualizar este dashboard."
      />
    );
  }
  if (state.status === "error") {
    return (
      <PageError
        title="Não foi possível carregar"
        description={state.message}
        retry={() => void load()}
      />
    );
  }

  const { data } = state;
  const cards = [
    {
      label: "Resolver agora",
      value: data.metrics.resolveNow,
      detail: "documentos que precisam de ação",
      icon: AlertTriangle,
      color: "text-red-700",
      background: "bg-red-50",
    },
    {
      label: "Vence em breve",
      value: data.metrics.expiringSoon,
      detail: "documentos que exigem atenção",
      icon: Clock3,
      color: "text-amber-700",
      background: "bg-amber-50",
    },
    {
      label: "Em análise",
      value: data.metrics.review,
      detail: "enviados, aguardando conferência",
      icon: Search,
      color: "text-blue-700",
      background: "bg-blue-50",
    },
    {
      label: "Conformidade",
      value: `${data.metrics.compliancePercent}%`,
      detail: `${data.required.met}/${data.required.total} obrigatórios`,
      icon: CheckCircle2,
      color: "text-teal-700",
      background: "bg-teal-50",
    },
  ];

  return (
    <div className="space-y-8">
      <header>
        <p className="text-sm font-medium text-teal-700">{me?.supplier.name}</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
          Olá, {me?.person.name.split(" ")[0]}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Acompanhe o que precisa da sua atenção e o progresso documental da
          empresa.
        </p>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <article
              key={card.label}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div
                className={`flex size-10 items-center justify-center rounded-xl ${card.background} ${card.color}`}
              >
                <Icon size={20} />
              </div>
              <p className="mt-4 text-sm text-slate-600">{card.label}</p>
              <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-950">
                {card.value}
              </p>
              <p className="mt-1 text-xs text-slate-500">{card.detail}</p>
            </article>
          );
        })}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-semibold text-slate-900">
              Documentos obrigatórios
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {data.required.met}/{data.required.total} atendidos
            </p>
          </div>
          <strong className="text-lg text-teal-700">
            {data.metrics.compliancePercent}%
          </strong>
        </div>
        <div
          className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={data.metrics.compliancePercent}
        >
          <div
            className="h-full rounded-full bg-teal-600 transition-[width]"
            style={{ width: `${data.metrics.compliancePercent}%` }}
          />
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Complementares: {data.optional.met}/{data.optional.total}. Eles não
          reduzem seu percentual de conformidade.
        </p>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Precisa de você agora
            </h2>
            <p className="text-sm text-slate-600">
              Pendências documentais ordenadas por urgência.
            </p>
          </div>
          <Button asChild variant="link" className="px-0">
            <Link to="/documentation">
              Ver documentação
              <ArrowRight size={16} />
            </Link>
          </Button>
        </div>
        {data.queue.length === 0 ? (
          <div className="rounded-2xl border border-teal-200 bg-teal-50 p-6">
            <CheckCircle2 className="text-teal-700" size={24} />
            <h3 className="mt-3 font-semibold text-teal-950">
              Nenhuma ação urgente
            </h3>
            <p className="mt-1 text-sm text-teal-800">
              Não há documentos críticos ou vencendo que dependam de você agora.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {data.queue.map((item) => (
              <RequirementRow
                key={item.requirement.cdreq}
                requirement={item.requirement}
                compact
                onChanged={load}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-slate-950">Módulos</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <ModuleCard
            icon={FileCheck2}
            title="Documentação"
            description={
              data.modules.documentation.pending > 0
                ? `${data.modules.documentation.pending} pendências precisam de atenção.`
                : "Documentação sem pendências urgentes."
            }
            href="/documentation"
          />
          <ModuleCard
            icon={ClipboardCheck}
            title="Não conformidades"
            description="Nenhuma não conformidade disponível neste portal."
            unavailable
          />
          <ModuleCard
            icon={ListChecks}
            title="Ações planejadas"
            description="Nenhuma ação planejada disponível neste portal."
            unavailable
          />
        </div>
      </section>
    </div>
  );
}

function ModuleCard({
  icon: Icon,
  title,
  description,
  href,
  unavailable,
}: {
  icon: typeof FileCheck2;
  title: string;
  description: string;
  href?: string;
  unavailable?: boolean;
}) {
  const content = (
    <>
      <div className="flex size-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
        <Icon size={20} />
      </div>
      <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
      <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
      {unavailable && (
        <span className="mt-4 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500">
          Sem dados disponíveis
        </span>
      )}
    </>
  );
  return href ? (
    <Link
      to={href}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-teal-400"
    >
      {content}
    </Link>
  ) : (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      {content}
    </article>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-8" aria-label="Carregando dashboard">
      <div className="h-20 w-72 animate-pulse rounded-xl bg-slate-200" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="h-40 animate-pulse rounded-2xl bg-slate-200"
          />
        ))}
      </div>
      <div className="h-32 animate-pulse rounded-2xl bg-slate-200" />
    </div>
  );
}
